import { faArrowRotateRight } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { GridColDef } from '@mui/x-data-grid'
import { Button, Stack } from '@vapor/material'
import { RichGraphQLDataGrid } from 'hic-components'
import { useTranslation } from 'react-i18next'
import { HoauReservedResourcesDocument, HoauReservedResourcesQuery, HoauReservedResourcesQueryVariables, ReservedResourceQueryFilter, useSendMessageForAutomationMutation } from 'src/api/api.generated'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import useSuccessSnackBar from 'src/app/hooks/success-snack-bar/use-success-snack-bar'
import { displayResourceType } from 'src/utils/resource-type'
import { useHicCommonDataGridProps } from 'src/app/components/hic-vapor/datagrid/datagrid'

type HoauResource = {
  id: string
  resourceType: string
  resource: string
  automationId: string | null
  guestNames: string
  hasResource: boolean
  reservedResourceId: string
}

export default function HoauGrid({ tenantId, propertyId, reservationId }: { tenantId: string; propertyId: string; reservationId: string }) {
  const { t } = useTranslation()
  const localizeString = useLocalizeString()
  const { hicCommonDataGridPremiumProps } = useHicCommonDataGridProps()
  const showError = useErrorSnackBar()
  const showSuccess = useSuccessSnackBar()
  const [sendMessageForAutomation, { loading }] = useSendMessageForAutomationMutation({
    onError: showError,
  })

  async function onForceUpdate(reservedResourceId: string) {
    const result = await sendMessageForAutomation({
      variables: {
        input: {
          tenantId,
          propertyId,
          reservationId,
          reservedResourceId,
        },
      },
    })

    if (result.data && !result.errors) {
      showSuccess(t('pms.booking.reservation.hoau.grid.state.success'))
    }
  }

  const columns = useColumns({
    onForceUpdate,
  })

  return (
    <RichGraphQLDataGrid<HoauReservedResourcesQuery, HoauResource, HoauReservedResourcesQueryVariables, ReservedResourceQueryFilter>
      query={HoauReservedResourcesDocument}
      {...hicCommonDataGridPremiumProps}
      loading={loading}
      data={(response) => {
        return (
          response?.reservedResources?.flatMap((reservedResource) =>
            reservedResource?.resourceOccupancies?.map((resourceOccupancy) => ({
              id: `${resourceOccupancy.start.toISOString()}--${resourceOccupancy.end.toISOString()}`,
              resourceType: displayResourceType(localizeString, resourceOccupancy.assignedResourceType),
              resource: resourceOccupancy.resource?.code ?? '',
              automationId: reservedResource.automationId ?? null,
              guestNames: reservedResource.guestOccupancies?.map((guestOccupancy) => guestOccupancy?.guest?.name ?? '')?.join(', ') ?? '',
              hasResource: Boolean(resourceOccupancy.resource),
              reservedResourceId: reservedResource.id,
            })),
          ) ?? []
        )
      }}
      columns={columns}
      pagination={false}
      getRowHeight={() => 'auto'}
      variables={(params) => ({
        ...params,
        filter: {
          tenantId: { eq: tenantId },
          propertyId: { eq: propertyId },
          reservationId: { eq: reservationId },
        },
      })}
    />
  )
}

function useColumns({ onForceUpdate }: { onForceUpdate: (reservedResourceId: string) => Promise<void> }): GridColDef[] {
  const { t } = useTranslation()

  return [
    {
      field: 'resource',
      headerName: t('pms.booking.reservation.hoau.grid.resource'),
      disableColumnMenu: true,
      sortable: true,
      width: 150,
    },
    {
      field: 'resourceType',
      headerName: t('pms.booking.reservation.hoau.grid.resourceType'),
      disableColumnMenu: true,
      sortable: true,
      width: 150,
    },
    {
      field: 'guestNames',
      headerName: t('pms.booking.reservation.hoau.grid.guests'),
      disableColumnMenu: true,
      sortable: true,
      flex: 1,
    },
    {
      field: 'automationId',
      headerName: t('pms.booking.reservation.hoau.grid.state'),
      disableColumnMenu: true,
      width: 250,
      sortable: false,
      renderCell: (params) => {
        const row = params.row as HoauResource

        return (
          <Stack gap={0.5}>
            {row.hasResource
              ? row.automationId
                ? t('pms.booking.reservation.hoau.grid.state.automation')
                : t('pms.booking.reservation.hoau.grid.state.no_automation')
              : t('pms.booking.reservation.hoau.grid.state.no_resource')}
            {row.hasResource && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<FontAwesomeIcon icon={faArrowRotateRight} />}
                onClick={() => {
                  onForceUpdate(row.reservedResourceId)
                }}
              >
                {t('pms.booking.reservation.hoau.grid.state.resend')}
              </Button>
            )}
          </Stack>
        )
      },
    },
  ]
}
